<script lang="ts">
  import { fade } from "svelte/transition";

  let showSuccessMsg = false;
  let formData = { name: "", email: "", phone: "" };

  function handleSubmit(e: Event) {
    e.preventDefault();
    showSuccessMsg = true;
    formData = { ...formData, name: "", email: "", phone: "" };
    setTimeout(() => {
      showSuccessMsg = false;
    }, 4000);
  }
</script>

<div class="w-full p-6 max-w-7xl mx-auto relative mb-20">
  {#if showSuccessMsg}
    <div
      class="fixed top-28 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-8 py-5 rounded-xl shadow-2xl z-50 flex items-center transition-opacity border-b-8 border-green-700"
      transition:fade
    >
      <i class="fa-solid fa-paper-plane text-4xl mr-5"></i>
      <div>
        <p class="text-2xl font-black uppercase tracking-wide">Thank You!</p>
        <p class="text-base font-medium mt-1">
          Your message has been received. We'll be in touch soon.
        </p>
      </div>
      <button
        class="ml-8 text-2xl hover:text-black transition"
        on:click={() => (showSuccessMsg = false)}
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>
  {/if}

  <div class="text-center mb-16 pt-4">
    <h2
      class="text-5xl font-black uppercase tracking-wider text-gray-800 mb-6 drop-shadow-sm"
    >
      Contact Us
    </h2>
    <p
      class="text-2xl text-gray-500 max-w-3xl mx-auto font-medium leading-relaxed"
    >
      Have a question or just want to say hi? Drop us a message below and our
      friendly team will get back to you within 24 hours.
    </p>
  </div>

  <div
    class="flex flex-col lg:flex-row bg-white rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-100 min-h-[600px]"
  >
    <!-- Form Side -->
    <div
      class="w-full lg:w-1/2 p-10 lg:p-14 bg-white flex flex-col justify-center"
    >
      <h3
        class="text-3xl font-extrabold mb-8 text-sky-500 uppercase tracking-widest"
      >
        <i class="fa-regular fa-envelope mr-3"></i> Get in Touch
      </h3>
      <form on:submit={handleSubmit} class="space-y-6">
        <div>
          <label
            for="name"
            class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide"
            >Full Name</label
          >
          <input
            type="text"
            id="name"
            maxlength="50"
            bind:value={formData.name}
            class="w-full p-4 text-lg bg-gray-50 border-2 border-gray-200 rounded-xl transition duration-300 hover:border-sky-400 focus:border-sky-500 focus:bg-white focus:shadow-md outline-none"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label
            for="email"
            class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide"
            >Email Address</label
          >
          <input
            type="email"
            id="email"
            bind:value={formData.email}
            class="w-full p-4 text-lg bg-gray-50 border-2 border-gray-200 rounded-xl transition duration-300 hover:border-sky-400 focus:border-sky-500 focus:bg-white focus:shadow-md outline-none"
            placeholder="john@example.com"
            required
          />
        </div>

        <div>
          <label
            for="phone"
            class="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide"
            >Contact Number</label
          >
          <input
            type="text"
            id="phone"
            on:input={(e) =>
              (formData.phone = e.currentTarget.value.replace(/[^0-9]/g, ""))}
            bind:value={formData.phone}
            class="w-full p-4 text-lg bg-gray-50 border-2 border-gray-200 rounded-xl transition duration-300 hover:border-sky-400 focus:border-sky-500 focus:bg-white focus:shadow-md outline-none"
            placeholder="(555) 123-4567"
            required
          />
        </div>

        <div class="pt-6 flex gap-4">
          <button
            type="submit"
            class="flex-1 bg-sky-500 text-white font-black text-lg py-4 rounded-xl shadow-lg hover:shadow-2xl hover:bg-amber-400 hover:text-black hover:-translate-y-1 transition duration-300 uppercase tracking-widest"
            >Submit Message</button
          >
          <button
            type="reset"
            class="px-8 bg-gray-200 text-gray-600 font-bold text-lg py-4 rounded-xl hover:bg-gray-300 hover:-translate-y-1 transition duration-300 uppercase tracking-widest"
            >Reset</button
          >
        </div>
      </form>
    </div>

    <!-- Info / Map Side -->
    <div
      class="w-full lg:w-1/2 bg-gray-50 flex flex-col relative min-h-[400px]"
    >
      <iframe
        class="absolute inset-0 w-full h-full border-0"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0963065355604!2d-122.40263668468212!3d37.78853157975685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80858062b083b483%3A0x6bbaaaaff3a9cfa2!2sSalesforce%20Park!5e0!3m2!1sen!2sus!4v1683141684000!5m2!1sen!2sus"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        title="Map Location"
      >
      </iframe>

      <div
        class="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 bg-sky-900/95 backdrop-blur-md text-white p-8 lg:p-10 rounded-3xl shadow-2xl border-t-4 border-amber-400"
      >
        <h4
          class="text-3xl font-black uppercase tracking-widest mb-6 text-amber-400"
        >
          <i class="fa-solid fa-location-arrow mr-2 text-2xl"></i> Reach out to us
        </h4>
        <div
          class="flex items-start mb-5 font-medium text-lg transition duration-300 hover:translate-x-2"
        >
          <i class="fa-solid fa-location-dot mt-1 mr-5 text-2xl text-sky-400"
          ></i>
          <p class="text-gray-100 leading-relaxed">
            124 Market Street, Suite 500<br />
            Financial District<br />
            San Francisco, CA 94105
          </p>
        </div>
        <div
          class="flex items-center mb-5 font-medium text-lg transition duration-300 hover:translate-x-2"
        >
          <i class="fa-solid fa-phone mr-5 text-2xl text-sky-400"></i>
          <p class="text-gray-100">+1 (800) 555-0199</p>
        </div>
        <div
          class="flex items-center font-medium text-lg transition duration-300 hover:translate-x-2"
        >
          <i class="fa-solid fa-envelope mr-5 text-2xl text-sky-400"></i>
          <p class="text-gray-100">support@globalbazar.com</p>
        </div>
      </div>
    </div>
  </div>
</div>
